import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import {
   listTransfersApi,
   cancelTransferApi,
   type ApiDeviceTransfer,
} from '@/api/vendo';
import { useUIStore } from '@/store/useUIStore';

/**
 * Pending transfers section, surfaced above the main devices table.
 *
 * Two stacked tables:
 *   - Outgoing — the operator initiated these as the seller. Device sits
 *     here (instead of the main devices table) until the buyer accepts,
 *     rejects, or the transfer expires. Per row: Copy link + Cancel.
 *   - Incoming — somebody else is offering a device to the current user.
 *     Per row: Review (opens ReviewTransfer modal).
 *
 * Auto-open: if the page URL has ?reviewTransfer=<id>, the matching
 * incoming row's Review modal opens once on mount. The email accept-link
 * and the seller's copy-link both use this query param.
 */

type Mode = 'outgoing' | 'incoming';

const PendingTransfers: React.FC = () => {
   const { data: outgoing = [] } = useQuery({
      queryKey: ['transfers', 'outgoing'],
      queryFn: () => listTransfersApi('outgoing'),
      refetchInterval: 60 * 1000,
   });
   const { data: incoming = [] } = useQuery({
      queryKey: ['transfers', 'incoming'],
      queryFn: () => listTransfersApi('incoming'),
      refetchInterval: 60 * 1000,
   });

   const pendingOutgoing = useMemo(
      () => outgoing.filter((t) => t.status === 'pending'),
      [outgoing]
   );
   const pendingIncoming = useMemo(
      () => incoming.filter((t) => t.status === 'pending'),
      [incoming]
   );

   const [params, setParams] = useSearchParams();
   const reviewId = params.get('reviewTransfer');
   const handleShow = useUIStore((s) => s.handleShow);
   const [autoOpened, setAutoOpened] = useState(false);

   useEffect(() => {
      if (!reviewId || autoOpened) return;
      const match = pendingIncoming.find((t) => t._id === reviewId);
      if (!match) return;
      handleShow({
         template: 'review-transfer',
         title: 'Review device transfer',
         data: { transfer: match },
      });
      setAutoOpened(true);
      // Clear the param so a future render doesn't reopen the modal.
      const next = new URLSearchParams(params);
      next.delete('reviewTransfer');
      setParams(next, { replace: true });
   }, [reviewId, pendingIncoming, autoOpened, handleShow, params, setParams]);

   if (pendingOutgoing.length === 0 && pendingIncoming.length === 0) {
      return null;
   }

   return (
      <Wrapper>
         {pendingOutgoing.length > 0 && (
            <OutgoingTable rows={pendingOutgoing} />
         )}
         {pendingIncoming.length > 0 && (
            <IncomingTable rows={pendingIncoming} />
         )}
      </Wrapper>
   );
};

export default PendingTransfers;

/* ─── Outgoing (seller POV) ────────────────────────────────── */

const OutgoingTable: React.FC<{ rows: ApiDeviceTransfer[] }> = ({ rows }) => {
   const queryClient = useQueryClient();
   const [copiedId, setCopiedId] = useState<string | null>(null);

   const handleCopyLink = async (t: ApiDeviceTransfer) => {
      const url = t.signupToken
         ? `${window.location.origin}/transfers/handoff?token=${encodeURIComponent(t.signupToken)}`
         : `${window.location.origin}/wifi-devices?reviewTransfer=${t._id}`;
      try {
         await navigator.clipboard.writeText(url);
         setCopiedId(t._id);
         toast.success('Link copied — send to the buyer');
         setTimeout(() => setCopiedId(null), 2000);
      } catch {
         toast.error('Copy failed');
      }
   };

   const handleCancel = async (t: ApiDeviceTransfer) => {
      const deviceId =
         typeof t.device === 'object' ? t.device._id : t.device;
      try {
         await cancelTransferApi(deviceId);
         toast.success('Pending transfer cancelled');
         queryClient.invalidateQueries({ queryKey: ['transfers'] });
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Cancel failed');
      }
   };

   return (
      <Section>
         <SectionHeader $mode="outgoing">
            <SectionTitle>
               <Dot $mode="outgoing" />
               Outgoing transfers
               <Count>{rows.length}</Count>
            </SectionTitle>
            <SectionSub>Waiting on the buyer to accept</SectionSub>
         </SectionHeader>

         <Table>
            <THead>
               <TR>
                  <TH>Device</TH>
                  <TH>Buyer</TH>
                  <TH>Type</TH>
                  <TH>Expires</TH>
                  <TH align="right">Action</TH>
               </TR>
            </THead>
            <TBody>
               {rows.map((t) => {
                  const dev = typeof t.device === 'object' ? t.device : null;
                  return (
                     <TR key={t._id}>
                        <TD>
                           <DeviceName>{dev?.name ?? '—'}</DeviceName>
                           {dev?.deviceId && <DeviceMeta>{dev.deviceId}</DeviceMeta>}
                        </TD>
                        <TD>{t.toEmail}</TD>
                        <TD>
                           <Badge $type={t.paymentType}>
                              {t.paymentType === 'full-cash'
                                 ? `Paid · ₱${(t.fullCashAmount ?? 0).toLocaleString()}`
                                 : t.paymentType === 'installment'
                                 ? 'Installment'
                                 : 'Plain Transfer'}
                           </Badge>
                        </TD>
                        <TD>{new Date(t.expiresAt).toLocaleDateString()}</TD>
                        <TD align="right">
                           <ActionRow>
                              <CopyBtn onClick={() => handleCopyLink(t)}>
                                 {copiedId === t._id ? 'Copied ✓' : 'Copy link'}
                              </CopyBtn>
                              <CancelBtn onClick={() => handleCancel(t)}>
                                 Cancel
                              </CancelBtn>
                           </ActionRow>
                        </TD>
                     </TR>
                  );
               })}
            </TBody>
         </Table>
      </Section>
   );
};

/* ─── Incoming (buyer POV) ─────────────────────────────────── */

const IncomingTable: React.FC<{ rows: ApiDeviceTransfer[] }> = ({ rows }) => {
   const handleShow = useUIStore((s) => s.handleShow);

   const openReview = (t: ApiDeviceTransfer) => {
      handleShow({
         template: 'review-transfer',
         title: 'Review device transfer',
         data: { transfer: t },
      });
   };

   return (
      <Section $mode="incoming">
         <SectionHeader $mode="incoming">
            <SectionTitle $mode="incoming">
               <Dot $mode="incoming" />
               Incoming transfers
               <Count $accent="incoming">{rows.length}</Count>
            </SectionTitle>
            <SectionSub>
               A seller is offering a device to your account.{' '}
               <SubCall>Review it before it expires.</SubCall>
            </SectionSub>
         </SectionHeader>

         <Table>
            <THead>
               <TR>
                  <TH>Device</TH>
                  <TH>From</TH>
                  <TH>Type</TH>
                  <TH>Expires</TH>
                  <TH align="right">Action</TH>
               </TR>
            </THead>
            <TBody>
               {rows.map((t) => {
                  const dev = typeof t.device === 'object' ? t.device : null;
                  const seller =
                     typeof t.fromOwner === 'object'
                        ? t.fromOwner.name || t.fromOwner.email
                        : t.fromOwner;
                  return (
                     <TR key={t._id}>
                        <TD>
                           <DeviceName>{dev?.name ?? '—'}</DeviceName>
                           {dev?.deviceId && <DeviceMeta>{dev.deviceId}</DeviceMeta>}
                        </TD>
                        <TD>{seller}</TD>
                        <TD>
                           <Badge $type={t.paymentType} $incoming>
                              {t.paymentType === 'full-cash'
                                 ? `Paid · ₱${(t.fullCashAmount ?? 0).toLocaleString()}`
                                 : t.paymentType === 'installment'
                                 ? 'Installment'
                                 : 'Plain Transfer'}
                           </Badge>
                        </TD>
                        <TD>{new Date(t.expiresAt).toLocaleDateString()}</TD>
                        <TD align="right">
                           <ReviewBtn onClick={() => openReview(t)}>
                              Review →
                           </ReviewBtn>
                        </TD>
                     </TR>
                  );
               })}
            </TBody>
         </Table>
      </Section>
   );
};

/**
 * Hook for parent components (DevicesTab) to learn which device IDs have
 * a pending outgoing transfer, so the main devices table can hide them.
 * Shares the same React Query cache key as the section above.
 */
export const usePendingOutgoingDeviceIds = (): Set<string> => {
   const { data: outgoing = [] } = useQuery({
      queryKey: ['transfers', 'outgoing'],
      queryFn: () => listTransfersApi('outgoing'),
      refetchInterval: 60 * 1000,
   });
   return useMemo(() => {
      const s = new Set<string>();
      outgoing
         .filter((t) => t.status === 'pending')
         .forEach((t) => {
            const id = typeof t.device === 'object' ? t.device._id : t.device;
            if (id) s.add(id);
         });
      return s;
   }, [outgoing]);
};

/* ─── Styled ───────────────────────────────────────────────── */

const Wrapper = styled.div`
   display: flex;
   flex-direction: column;
   gap: 16px;
   margin-bottom: 20px;
`;

// Incoming = something waiting on THIS user, so it wears orange and a soft
// glow; a pale blue card was easy to miss (Ray, 2026-10-29, A50).
const ORANGE_INK = '#c2410c';
const ORANGE_INK_DARK = '#ffb26b';

const pulse = keyframes`
   70% { box-shadow: 0 0 0 7px ${colors.ORANGE}00; }
   100% { box-shadow: 0 0 0 0 ${colors.ORANGE}00; }
`;

const Section = styled.section<{ $mode?: Mode }>`
   background: ${colors.WHITE};
   border: ${({ $mode }) =>
      $mode === 'incoming' ? `1.5px solid ${colors.ORANGE}` : `1px solid ${colors.GRAY12}`};
   box-shadow: ${({ $mode }) =>
      $mode === 'incoming' ? `0 0 0 3px ${colors.ORANGE}24` : 'none'};
   border-radius: 12px;
   overflow: hidden;
`;

const SectionHeader = styled.div<{ $mode: Mode }>`
   padding: 14px 18px;
   background: ${({ $mode }) =>
      $mode === 'incoming' ? `${colors.ORANGE}24` : `${colors.WARNING}10`};
   border-bottom: 1px solid
      ${({ $mode }) =>
         $mode === 'incoming' ? `${colors.ORANGE}59` : `${colors.WARNING}25`};
`;

const SectionTitle = styled.div<{ $mode?: Mode }>`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 14px;
   font-weight: 700;
   color: ${({ $mode }) => ($mode === 'incoming' ? ORANGE_INK : colors.GRAY_DARK)};
   [data-theme='dark'] & {
      color: ${({ $mode }) => ($mode === 'incoming' ? ORANGE_INK_DARK : colors.GRAY_DARK)};
   }
`;

const SectionSub = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
   margin-top: 2px;
   margin-left: 18px;
`;

const SubCall = styled.b`
   color: ${ORANGE_INK};
   [data-theme='dark'] & {
      color: ${ORANGE_INK_DARK};
   }
`;

const Dot = styled.span<{ $mode: Mode }>`
   width: 8px;
   height: 8px;
   border-radius: 50%;
   background: ${({ $mode }) =>
      $mode === 'incoming' ? colors.ORANGE : colors.WARNING};
   animation: ${({ $mode }) => ($mode === 'incoming' ? pulse : 'none')} 1.6s infinite;
   @media (prefers-reduced-motion: reduce) {
      animation: none;
   }
`;

const Count = styled.span<{ $accent?: Mode }>`
   font-size: 11px;
   font-weight: 700;
   color: ${({ $accent }) =>
      $accent === 'incoming' ? '#fff' : colors.WARNING};
   background: ${({ $accent }) =>
      $accent === 'incoming' ? colors.ORANGE : `${colors.WARNING}1f`};
   padding: 2px 8px;
   border-radius: 999px;
   margin-left: 4px;
`;

const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
`;

const THead = styled.thead`
   background: ${colors.GRAY14};
`;

const TBody = styled.tbody``;

const TR = styled.tr`
   border-bottom: 1px solid ${colors.GRAY12};
   &:last-child {
      border-bottom: none;
   }
`;

const TH = styled.th<{ align?: 'left' | 'right' }>`
   text-align: ${({ align }) => align ?? 'left'};
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   padding: 10px 14px;
`;

const TD = styled.td<{ align?: 'left' | 'right' }>`
   text-align: ${({ align }) => align ?? 'left'};
   padding: 12px 14px;
   color: ${colors.GRAY_DARK};
   vertical-align: middle;
`;

const DeviceName = styled.div`
   font-weight: 700;
   font-size: 13px;
`;

const DeviceMeta = styled.div`
   font-family: 'SF Mono', Menlo, monospace;
   font-size: 10px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const Badge = styled.span<{
   $type: 'free' | 'full-cash' | 'installment';
   $incoming?: boolean;
}>`
   padding: 3px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   background: ${({ $type, $incoming }) =>
      $type === 'full-cash'
         ? `${colors.SUCCESS}1f`
         : $type === 'installment'
         ? `${colors.WARNING}26`
         : $incoming
         ? `${colors.ORANGE}24`
         : `${colors.PRIMARY}15`};
   color: ${({ $type, $incoming }) =>
      $type === 'full-cash'
         ? colors.SUCCESS
         : $type === 'installment'
         ? colors.WARNING
         : $incoming
         ? ORANGE_INK
         : colors.PRIMARY};
`;

const ActionRow = styled.div`
   display: inline-flex;
   gap: 6px;
   justify-content: flex-end;
`;

const CopyBtn = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 6px;
   padding: 6px 12px;
   font-size: 11px;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      opacity: 0.92;
   }
`;

const CancelBtn = styled.button`
   background: transparent;
   color: ${colors.DANGER};
   border: 1px solid ${colors.DANGER}50;
   border-radius: 6px;
   padding: 6px 12px;
   font-size: 11px;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      background: ${colors.DANGER}10;
   }
`;

const ReviewBtn = styled.button`
   background: ${colors.ORANGE};
   color: #fff;
   border: none;
   border-radius: 6px;
   padding: 7px 14px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      opacity: 0.92;
   }
`;
